import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import CloseIcon from "@mui/icons-material/Close";
import ConstructionIcon from "@mui/icons-material/Construction";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router";
import {
  useContentList,
  useEquipmentSearch,
  useRigOfWeek,
  type EquipmentSearchHit,
} from "../hooks/useApiQueries.js";
import { Box as MuiBox } from "@mui/material";
import { ContentCard } from "../components/ContentCard.js";
import { HeroVideoCarousel } from "../components/HeroVideoCarousel.js";
import { VideoLightbox } from "../components/VideoLightbox.js";
import { SEO } from "../components/SEO.js";
import { NeonEyebrow, neonButtonSx, NEON_GLOW_SOFT, NEON_GLOW } from "../components/Neon.js";
import { useAuth } from "../hooks/useAuth.js";
import { useVideoLightbox } from "../hooks/useVideoLightbox.js";
import { proxied, isLikelyImageUrl } from "../lib/imageProxy.js";

export function Watch() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") ?? 1);
  const postcode = params.get("postcode") ?? "";
  const equipmentSlug = params.get("equipmentSlug") ?? "";
  const equipmentLabel = params.get("equipmentLabel") ?? equipmentSlug;
  const [postcodeInput, setPostcodeInput] = useState(postcode);
  const [equipmentInput, setEquipmentInput] = useState("");
  const { openFor: openLightbox, lightboxProps } = useVideoLightbox();
  const { user } = useAuth();

  const { data, isLoading } = useContentList({
    page,
    type: "VIDEO",
    postcode: postcode || undefined,
    equipmentSlug: equipmentSlug || undefined,
  });
  // Pull 5 latest videos for the hero carousel. Lightweight — same endpoint,
  // just a tiny pageSize, dedup'd by react-query against the main list.
  const { data: heroVideosRes } = useContentList({ type: "VIDEO", pageSize: 5 });
  const { data: rig } = useRigOfWeek();

  const { data: equipmentResults, isFetching: equipmentFetching } = useEquipmentSearch(equipmentInput);

  const clearFilter = (key: string) => {
    const next = new URLSearchParams(params);
    next.delete(key);
    if (key === "equipmentSlug") next.delete("equipmentLabel");
    next.set("page", "1");
    if (key === "postcode") setPostcodeInput("");
    setParams(next);
  };

  const setEquipmentFilter = (hit: EquipmentSearchHit | null) => {
    const next = new URLSearchParams(params);
    if (hit) {
      next.set("equipmentSlug", hit.slug);
      next.set("equipmentLabel", hit.name);
    } else {
      next.delete("equipmentSlug");
      next.delete("equipmentLabel");
    }
    next.set("page", "1");
    setEquipmentInput("");
    setParams(next);
  };

  const hasActiveFilters = !!(postcode || equipmentSlug);

  return (
    <>
      <SEO
        title="Watch — Australian earthmoving videos"
        description="Curated earthmoving and mining videos from across Australia. Real machines, real operators, real dirt."
        canonical="https://corianpark.au/watch"
      />

      <HeroVideoCarousel
        videos={heroVideosRes?.data ?? []}
        eyebrow="WATCH · CURATED VIDEOS"
        height={{ xs: 360, md: 480 }}
        title={
          <>
            The best earthmoving footage,{" "}
            <MuiBox component="span" sx={{ color: "primary.main", textShadow: NEON_GLOW }}>
              in one place.
            </MuiBox>
          </>
        }
      />

      {/* Rig of the Week featured strip */}
      {rig && (
        <Box sx={{ mb: 4 }}>
          <RouterLink to="/rig-of-the-week" style={{ textDecoration: "none", color: "inherit" }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
                alignItems: "center",
                transition: "transform 200ms ease, box-shadow 200ms ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: NEON_GLOW_SOFT,
                  borderColor: "primary.main",
                },
              }}
            >
              {isLikelyImageUrl(rig.equipment?.machinery?.primaryImageUrl ?? rig.imageUrl) && (
                <Box
                  component="img"
                  src={proxied((rig.equipment?.machinery?.primaryImageUrl ?? rig.imageUrl)!)}
                  alt={rig.equipment?.machinery?.name ?? "Rig of the Week"}
                  loading="lazy"
                  sx={{ width: { xs: "100%", sm: 160 }, height: { xs: 180, sm: 100 }, objectFit: "cover", borderRadius: 1 }}
                />
              )}
              <Box sx={{ flex: 1 }}>
                <NeonEyebrow>RIG OF THE WEEK</NeonEyebrow>
                <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>
                  {rig.equipment?.machinery?.brand?.name ? `${rig.equipment.machinery.brand.name} ` : ""}
                  {rig.equipment?.machinery?.name ?? "This week's pick"}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", color: "primary.main", fontWeight: 700 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>See the breakdown</Typography>
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Stack>
            </Stack>
          </RouterLink>
        </Box>
      )}

      {/* Search filters row */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: hasActiveFilters ? 1.5 : 3, alignItems: { sm: "center" }, flexWrap: "wrap" }}
      >
        <TextField
          size="small"
          label="Postcode"
          value={postcodeInput}
          onChange={(e) => setPostcodeInput(e.target.value.replace(/[^\d]/g, "").slice(0, 4))}
          placeholder="e.g. 3525"
          slotProps={{
            htmlInput: { maxLength: 4, inputMode: "numeric", "aria-label": "Filter by postcode" },
          }}
          sx={{ minWidth: 140 }}
        />

        <Autocomplete
          size="small"
          sx={{ minWidth: 220, flex: { sm: 1 }, maxWidth: { sm: 340 } }}
          options={equipmentResults?.data ?? []}
          getOptionLabel={(o) => o.name}
          inputValue={equipmentInput}
          onInputChange={(_, v) => setEquipmentInput(v)}
          onChange={(_, v) => setEquipmentFilter(v)}
          loading={equipmentFetching}
          noOptionsText={equipmentInput.length < 2 ? "Type to search equipment…" : "No equipment found"}
          renderInput={(inputParams) => (
            <TextField
              {...inputParams}
              label="Equipment"
              placeholder="e.g. JCB Loadall"
              slotProps={{
                ...inputParams.slotProps,
                input: {
                  ...(inputParams.slotProps?.input as object | undefined),
                  endAdornment: (
                    <>
                      {equipmentFetching && <CircularProgress size={14} sx={{ mr: 0.5 }} />}
                      {(inputParams.slotProps?.input as { endAdornment?: React.ReactNode } | undefined)?.endAdornment}
                    </>
                  ),
                },
                htmlInput: {
                  ...(inputParams.slotProps?.htmlInput as object | undefined),
                  "aria-label": "Filter by equipment",
                },
              }}
            />
          )}
        />

        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const next = new URLSearchParams(params);
            if (postcodeInput) next.set("postcode", postcodeInput);
            else next.delete("postcode");
            next.set("page", "1");
            setParams(next);
          }}
          sx={neonButtonSx}
        >
          Filter
        </Button>
      </Stack>

      {hasActiveFilters && (
        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: "wrap", gap: 1, alignItems: "center" }}>
          <Typography variant="caption" color="text.secondary">Active filters:</Typography>
          {postcode && (
            <Chip
              icon={<PlaceIcon sx={{ fontSize: 14 }} />}
              label={`Postcode ${postcode}`}
              deleteIcon={<CloseIcon />}
              onDelete={() => clearFilter("postcode")}
              color="primary"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          )}
          {equipmentSlug && (
            <Chip
              icon={<ConstructionIcon sx={{ fontSize: 14 }} />}
              label={equipmentLabel}
              deleteIcon={<CloseIcon />}
              onDelete={() => clearFilter("equipmentSlug")}
              color="primary"
              size="small"
              sx={{ fontWeight: 700 }}
            />
          )}
        </Stack>
      )}

      {isLoading && (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Skeleton variant="rectangular" height={240} />
            </Grid>
          ))}
        </Grid>
      )}

      {!isLoading && data?.data.length === 0 && (
        <Alert severity="info">No videos found for your filters.</Alert>
      )}

      <Grid container spacing={2}>
        {data?.data.map((c) => (
          <Grid key={c.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <ContentCard item={c} showSaveButton onVideoClick={openLightbox} />
          </Grid>
        ))}
      </Grid>

      <VideoLightbox {...lightboxProps} />

      {data && data.totalPages > 1 && (
        <Box sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
          <Pagination
            count={data.totalPages}
            page={page}
            onChange={(_, p) => {
              const next = new URLSearchParams(params);
              next.set("page", String(p));
              setParams(next);
            }}
            color="primary"
          />
        </Box>
      )}

      {!user && (
        <Box
          sx={{
            mt: 6,
            py: 5,
            px: 3,
            textAlign: "center",
            border: "1px dashed",
            borderColor: "primary.main",
            borderRadius: 1,
            background: "linear-gradient(135deg, rgba(255,176,0,0.08) 0%, transparent 70%)",
          }}
        >
          <NeonEyebrow glow>STICK AROUND</NeonEyebrow>
          <Typography variant="h4" sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, mt: 1, mb: 1 }}>
            Sign up free to see more of what you love
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, maxWidth: 480, mx: "auto" }}>
            Save the videos you actually want to come back to. Build your Loader Bucket.
          </Typography>
          <Button
            component={RouterLink}
            to="/auth/sign-in"
            variant="contained"
            color="primary"
            size="large"
            sx={neonButtonSx}
          >
            Create a free profile
          </Button>
        </Box>
      )}
    </>
  );
}
